import { TeamSwitcher } from '@/components/Menu/TeamSwitcher/TeamSwitcher';
import AuditOutlined from '@ant-design/icons/AuditOutlined';
import DatabaseOutlined from '@ant-design/icons/DatabaseOutlined';
import DollarOutlined from '@ant-design/icons/DollarOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import KeyOutlined from '@ant-design/icons/KeyOutlined';
import PartitionOutlined from '@ant-design/icons/PartitionOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import { type MenuProps } from 'antd/es/menu';
import Popover from 'antd/es/popover';
import { Link, useParams } from 'react-router-dom';

export const useNavigationMenu = ({ mode }: { mode: 'sidebar' | 'drawer' }) => {
  const { teamId } = useParams<{ teamId: string }>();
  const hasValidTeam = !!teamId;

  const getSelectedKey = () => {
    if (!hasValidTeam) return '';
    const path = location.pathname;
    if (path.endsWith(`/team/${teamId}`)) return 'home';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/members')) return 'members';
    if (path.includes('/datasets')) return 'datasets';
    if (path.includes('/files')) return 'files';
    if (path.includes('/agents')) return 'agents';
    if (path.includes('/pipelines')) return 'pipelines';
    if (path.includes('/audit-logs')) return 'audit-logs';
    if (path.includes('/api-tokens')) return 'api-tokens';
    // TODO: add remaining routes here (billing)
    return 'home';
  };

  const items: MenuProps['items'] = [
    ...(mode === 'sidebar'
      ? [
          {
            key: 'team-switcher',
            type: 'item' as const,
            label: <TeamSwitcher />,
            icon: <TeamOutlined />,
            title: '',
          },
        ]
      : []),
    {
      type: 'group',
      label: 'Workspace',
      children: [
        {
          key: 'home',
          icon: <HomeOutlined />,
          label: <Link to={`/team/${teamId}`}>Dashboard</Link>,
          disabled: !hasValidTeam,
        },
        {
          key: 'members',
          icon: <TeamOutlined />,
          label: <Link to={`/team/${teamId}/members`}>Team Members</Link>,
          disabled: !hasValidTeam,
        },
      ],
    },
    {
      type: 'group',
      label: 'Data',
      children: [
        {
          key: 'datasets',
          icon: <DatabaseOutlined />,
          label: <Link to={`/team/${teamId}/datasets`}>Datasets</Link>,
          disabled: !hasValidTeam,
        },
        {
          key: 'files',
          icon: <FileTextOutlined />,
          label: <Link to={`/team/${teamId}/files`}>Files</Link>,
          disabled: !hasValidTeam,
        },
      ],
    },
    {
      type: 'group',
      label: 'Automation',
      children: [
        {
          key: 'agents',
          icon: <RobotOutlined />,
          label: <Link to={`/team/${teamId}/agents`}>Agents</Link>,
          disabled: !hasValidTeam,
        },
        {
          key: 'pipelines',
          icon: <PartitionOutlined />,
          label: <Link to={`/team/${teamId}/pipelines`}>Pipelines</Link>,
          disabled: !hasValidTeam,
        },
      ],
    },
    {
      type: 'group',
      label: 'Security',
      children: [
        {
          key: 'api-tokens',
          icon: <KeyOutlined />,
          label: <Link to={`/team/${teamId}/api-tokens`}>API Tokens</Link>,
          disabled: !hasValidTeam,
        },
        {
          key: 'audit-logs',
          icon: <AuditOutlined />,
          label: <Link to={`/team/${teamId}/audit-logs`}>Audit Logs</Link>,
          disabled: !hasValidTeam,
        },
      ],
    },
    {
      type: 'group',
      label: 'Account',
      children: [
        {
          key: 'billing',
          icon: <DollarOutlined />,
          label: (
            <Popover
              content="This feature is coming soon! For any billing questions or concerns, please contact us directly at billing@moderately.ai"
              placement="right"
            >
              <span>Billing</span>
            </Popover>
          ),
          disabled: true, // Always disabled since it's coming soon
        },
      ],
    },
  ];

  return { items, selectedKeys: [getSelectedKey()] };
};
